import {
  Card,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography
} from '@mui/material';
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { apiService } from '../../api/axiosClient';
import CustomChip from '../../components/mui/chip';
import Spinner from '../../components/spinner';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import ActivityToolbar from './ActivityToolbar';

const TableActivity = props => {
  const {reload, setReload, setActivityID} = props;
  const [loading, setLoading] = useState(true)  
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [sort, setSort] = useState('desc')
  const [pageSize, setPageSize] = useState(10)
  const [list, setList] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [sortColumn, setSortColumn] = useState('id')
  const [selected, setSelected] = useState([]);

  const loadActivity = async (sort, query, column) => {
      try {
        const response = await apiService.get('/activity', {
          params: {
            query,
            sort,
            column,
            page,
            pageSize
          }
        });
        const payload = response.data;
        setTotal(payload.size);
        setList(payload.list);
        setLoading(false);
      } catch (e) {
        console.error('Error loading activity data:', e);
      }
    }
    
  useEffect(() => {
    loadActivity(sort, searchValue, sortColumn)
  }, [page, pageSize, reload])

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadActivity(newModel[0].sort, searchValue, newModel[0].field)
    } else {
      setSort('desc')
      setSortColumn('id')
    }
  }

  const handleSearch = value => {
    setSearchValue(value)
    loadActivity(sort, value, sortColumn)
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value)
  }

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  const columns = [
    {
      minWidth: 50,
      field: 'id',
      headerName: '활동 ID',
      renderCell: params => (
        <a href='#' onClick={() => setActivityID(params.row.id)} >
          <CustomChip
            size='small'
            skin='light'
            color='primary'
            label={params.row.id}
            sx={{ '& .MuiChip-label': { textTransform: 'capitalize' }, padding: 2, cursor: 'pointer' }}
          />
        </a>
      )
    },
    {
      field: 'activity_date',
      headerName: '활동일자',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {moment(params.row.activity_date).format("YYYY-MM-DD")}
        </Typography>
      )
    },
    {
      field: 'activity_type',
      headerName: '활동유형',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.activity_type}
        </Typography>
      )
    },
    {
      minWidth: 200,
      field: 'activity_title',
      headerName: '활동제목',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.activity_title}
        </Typography>
      )
    },
    {
      minWidth: 140,
      field: 'company_meeting_participants',
      headerName: '업체회의 참석자',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.company_meeting_participants}
        </Typography>
      )
    },
    {
      field: 'meeting_participants',
      headerName: '회의 참석자',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.meeting_participants}
        </Typography>
      )
    },
    {
      minWidth: 200,
      field: 'content',
      headerName: '내용',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.content}
        </Typography>
      )
    },
    {
      minWidth: 200,
      field: 'result',
      headerName: '결과',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.result}
        </Typography>
      )
    },
    {
      field: 'registration_date',
      minWidth: 140,
      headerName: '등록일자',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {moment(params.row.registration_date).format("YYYY-MM-DD")}
        </Typography>
      )
    }
  ]

  return (
    loading ?
    <Spinner/>
    :
    <Card>
      <CardHeader title='활동 이력' />
      <DataGrid
        sx={{
          ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
        }}
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        getRowId={row => row.id}
        pagination
        rows={list}
        rowCount={total || 0}
        columns={columns}
        pageSize={pageSize}
        sortingMode='server'
        paginationMode='server'
        onSortModelChange={handleSortModel}
        onPageChange={newPage => setPage(newPage)}
        components={{
          Toolbar: ActivityToolbar,
          Pagination: CustomPagination,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        componentsProps={{
          toolbar: {
            value: searchValue,
            clearSearch: () => handleSearch(''),
            onChange: event => handleSearch(event.target.value),
            numSelected: selected.length,
            selected,
            setReload
          }
        }}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(ids) => {
          const selectedIDs = new Set(ids);
          const selectedRows = list.filter((row) =>
            selectedIDs.has(row.id),
          );

          setSelected(selectedRows)
        }}
        experimentalFeatures={{ newEditingApi: true }}
      />
    </Card>
  )
}

export default TableActivity
